import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useFetchSurgerie } from './hook/useFetchSurgerie';
import Print from 'components/print/Print';
import { PrintConsumer } from 'components/print/hook/usePrint';
import { IconButton, styled, Typography } from '@mui/material';
import { Close, Print as PrintIcon } from '@mui/icons-material';
import { moneyFormat } from 'helpers/moneyFormat';
import history from 'services/history';
import { format } from 'date-fns';
import NoData from 'components/no-data/NoData';
import SavePdfButton from 'components/print/SavePdfButton';
import { pdfFileName } from 'helpers/exportPdf';

const HeaderActions = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  top: 10,
  right: 10,
  position: 'absolute',
  '@media print': {
    display: 'none',
  },
});

const WaveHeader = styled('div')({
  position: 'relative',
  width: '100%',
  height: 80,
  overflow: 'hidden',
  zIndex: 1,
  marginTop: -20,
  '& svg': {
    display: 'block',
    width: '100%',
    height: '100%',
  },
  '@media print': {
    WebkitPrintColorAdjust: 'exact',
    printColorAdjust: 'exact',
  },
});

const PrintPage = styled('div')({
  paddingBottom: 40,
  maxWidth: '300mm',
  position: 'relative',
  '@media print': {
    width: '210mm',
    minHeight: '297mm',
    boxShadow: 'none',
    padding: '10mm',
    '-webkit-print-color-adjust': 'exact',
    printColorAdjust: 'exact',
    '& p': {
      fontSize: 10,
      lineHeight: 1.2,
    },
    '& h6': {
      fontSize: 12,
      lineHeight: 1.2,
    },
  },
});

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  position: 'relative',
  '& li': {
    listStyleType: 'disc',
    marginLeft: 16,
  },
});

const Header = styled('div')({
  padding: 0,
  display: 'grid',
  alignItems: 'center',
  gridTemplateColumns: '200px 1fr',
  rowGap: 0,
  columnGap: 4,
  position: 'relative',
  zIndex: 2,
  marginTop: -40,
  '@media print': {
    marginTop: -50,
    columnGap: 2,
  },
});

const Img = styled('img')({
  width: 200,
  height: 100,
  objectFit: 'cover',
  zIndex: 3,
  '@media print': {
    objectFit: 'cover',
  },
});

const Infos = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  flex: 3,
});

const DateContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
});

const Subtitle = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
});

const Content = styled('div')({
  display: 'grid',
  border: '1px solid #000',
  borderRadius: 8,
  gridTemplateColumns: 'repeat(1, 1fr)',

  '& > *': {
    borderBottom: '1px solid #000',
  },

  '& > *:last-child': {
    borderBottom: 'none',
  },
});

const Box = styled('div')({
  display: 'grid',
  gridTemplateColumns: '200px 1fr',
  alignItems: 'center',
  padding: 4,
  '@media print': {
    padding: 2,
  },
});

const List = styled('div')({
  display: 'grid',
  gridTemplateColumns: '60px 1fr 120px',
  alignItems: 'center',
  borderLeft: '1px solid #000',
  borderRight: '1px solid #000',
  borderBottom: '1px solid #000',
  borderRadius: 16,

  '&:first-of-type': {
    borderTop: '1px solid #000',
  },

  '& > *': {
    borderRight: '1px solid #000',
    padding: 2,
  },

  '& > *:last-child': {
    borderRight: 'none',
  },

  '&.header': {
    fontWeight: 600,

    '@media print': {
      '-webkit-print-color-adjust': 'exact',
    },
  },
});

const Flex = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  '& > span': {
    padding: 0,
    width: '1em',
    height: '1em',
  },
});

const Table = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  marginTop: 8,
  '@media print': {
    marginTop: 0,
  },
});

const PrintSurgerie: React.FC = () => {
  const { status, id } = useParams<{ status: string; id: string }>();
  const { surgerie } = useFetchSurgerie(status, id);
  const pageRef = useRef<HTMLDivElement>(null);
  // quem abriu pode pedir para voltar a outra tela (ex.: ficha do paciente)
  const returnTo = (history.location.state as { returnTo?: string } | null)?.returnTo;

  return (
    <Print
      onExited={() => {
        history.push(returnTo || `/surgeries/${status}`);
      }}
    >
      {surgerie ? (
        <PrintConsumer>
          {context => (
            <>
              <HeaderActions>
                <SavePdfButton
                  targetRef={pageRef}
                  filename={pdfFileName('Orcamento', surgerie.id_int, surgerie.patient?.name)}
                />
                <IconButton onClick={window.print}>
                  <PrintIcon fontSize="small" />
                </IconButton>
                <IconButton onClick={context.handleClose}>
                  <Close fontSize="small" />
                </IconButton>
              </HeaderActions>
              <PrintPage ref={pageRef}>
                <Container>
                  <WaveHeader>
                    <svg viewBox="0 0 1440 80" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="waveGradientUP" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#4a0e1a" />
                          <stop offset="50%" stopColor="#6a1a2c" />
                          <stop offset="100%" stopColor="#4a0e1a" />
                        </linearGradient>
                      </defs>

                      <path
                        d="M0,50
     C240,75 480,20 720,30
     960,40 1200,60 1440,50
     L1440,0 L0,0 Z"
                        fill="url(#waveGradientUP)"
                      />
                    </svg>
                  </WaveHeader>
                  <Header>
                    <Img
                      src="https://h2i.s3.sa-east-1.amazonaws.com/upload/images/170245202601026958246571cc3.png"
                      alt="Logo"
                    />
                    <Infos>
                      <Flex>
                        <Typography variant="body2">
                          Avenida Altamirando de Araújo Ramos, 228. Sala 02, Andar 02, Centro - Simões Filho - BA
                        </Typography>
                      </Flex>
                      <Flex>
                        <Typography variant="body2">Celular/WhatsApp: (71) 9 9342-8102</Typography>
                      </Flex>
                      <Flex>
                        <Typography variant="body2">E-mail: contato@universoplastica.com.br</Typography>
                      </Flex>
                    </Infos>
                  </Header>

                  <Subtitle>
                    <Typography variant="h6" align="center">
                      ORÇAMENTO DE PACOTE CIRÚRGICO, Nº {surgerie?.id_int}
                    </Typography>
                    <DateContainer>
                      <Typography variant="body2" color="textSecondary">
                        {format(surgerie.closed_at || surgerie?.updated_at, 'dd/MM/yyyy')},{' '}
                        {format(surgerie.closed_at || surgerie?.updated_at, 'HH:mm')}
                      </Typography>
                    </DateContainer>
                  </Subtitle>

                  <Typography variant="subtitle2">Dados do paciente</Typography>
                  <Content>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Paciente / Cliente
                      </Typography>
                      <Typography variant="body2">{surgerie?.patient?.name}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        CPF
                      </Typography>
                      <Typography variant="body2">{surgerie?.patient?.document_number}</Typography>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Telefone
                      </Typography>

                      <Typography variant="body2">{surgerie?.patient?.phone}</Typography>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Endereço
                      </Typography>
                      {surgerie?.patient?.address && (
                        <Typography variant="body2">
                          {surgerie?.patient?.address?.street}, {surgerie?.patient?.address?.street_number}.{' '}
                          {surgerie?.patient?.address?.neighborhood} - {surgerie?.patient?.address?.city} -{' '}
                          {surgerie?.patient?.address?.state}
                        </Typography>
                      )}
                    </Box>
                  </Content>

                  <Typography variant="subtitle2">Dados do procedimento</Typography>
                  <Content>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Médico Executante
                      </Typography>
                      <Typography variant="body2">{surgerie?.doctor?.doctor?.name}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Regime de Atendimento
                      </Typography>
                      <Typography variant="body2">Diária DAY</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Hospital
                      </Typography>
                      <Typography variant="body2">{surgerie?.hospital?.company_name}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Tipo de Atendimento
                      </Typography>
                      <Typography variant="body2">CIRÚRGICO</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Procedimento
                      </Typography>
                      <Typography variant="body2">{surgerie?.procedure?.name}</Typography>
                    </Box>
                  </Content>

                  <Typography variant="subtitle2" fontWeight={600}>
                    1. Itens contemplados no pacote cirúrgico:
                  </Typography>

                  <Table>
                    <List className="header">
                      <Typography variant="body2" align="center" fontWeight={600}>
                        Item
                      </Typography>
                      <Typography variant="body2" align="left" fontWeight={600}>
                        Descrição
                      </Typography>
                    </List>
                    {surgerie?.packages.map((item, index) => (
                      <List key={item.id}>
                        <Typography variant="body2" align="center">
                          {index + 1}
                        </Typography>
                        <Typography variant="body2" align="left">
                          {item.package?.name}
                        </Typography>
                        <Typography variant="body2" align="center"></Typography>
                      </List>
                    ))}
                    <Typography
                      variant="body2"
                      align="center"
                      fontWeight={600}
                      style={{
                        borderLeft: '1px solid #000',
                        borderRight: '1px solid #000',
                        borderBottom: '1px solid #000',
                        borderRadius: 16,
                        padding: 2,
                      }}
                    >
                      Médico
                    </Typography>
                    <List>
                      <Typography variant="body2" align="center">
                        {surgerie ? surgerie?.packages.length + 1 : 1}
                      </Typography>
                      <Typography variant="body2" align="left">
                        {surgerie?.doctor?.doctor?.name}
                      </Typography>
                      <Typography variant="body2" align="right" fontWeight={600}></Typography>
                    </List>

                    <List>
                      <Typography variant="body2" align="center">
                        {surgerie ? surgerie?.packages.length + 2 : 2}
                      </Typography>
                      <Typography variant="body2" align="left">
                        ANESTESISTA
                      </Typography>
                      <Typography variant="body2" align="right" fontWeight={600}></Typography>
                    </List>
                  </Table>
                  <Typography variant="body2" align="right" fontWeight={600} style={{ marginRight: 8, fontSize: 18 }}>
                    {moneyFormat(surgerie?.value_total)}
                  </Typography>

                  <Typography variant="subtitle2" fontWeight={600}>
                    2. Orçamento:
                  </Typography>
                  <Typography variant="body2">
                    O orçamento para o Pacote Cirúrgico contemplando os serviços descritos no item 1 da presente
                    proposta é de <strong>{moneyFormat(surgerie?.value_total)}</strong>
                  </Typography>

                  <Typography variant="subtitle2" fontWeight={600}>
                    3. Observações:
                  </Typography>

                  <ul>
                    <li>
                      <Typography variant="body2">
                        O pacote orçado exclui intercorrências geradas pelo tratamento proposto;
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        O pacote orçado exclui consultas e exames pré-operatórios e pós-operatórios além dos
                        especificamente relacionados acima;
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        O pacote orçado exclui utilização de materiais e OPME&apos;s além dos relacionados acima;
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        O pacote orçado não contempla exames diagnósticos (tomografias, ressonâncias, ultrassonografias,
                        etc.) não relacionados acima;
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        Não estão contemplados no orçamento eventuais diárias hospitalares e de UTI acima das
                        quantidades especificadas neste orçamento;
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        As responsabilidades técnicas do procedimento são de responsabilidade exclusiva das equipes
                        envolvidas;
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        O valor orçado pode ser pago à vista, PIX parcelado, cartão de crédito em até 24x (com acréscimo
                        da maquina), boleto em até 10x.
                      </Typography>
                    </li>
                  </ul>
                </Container>
              </PrintPage>
            </>
          )}
        </PrintConsumer>
      ) : (
        <NoData message="Orçamento não encontrado." />
      )}
    </Print>
  );
};

export default PrintSurgerie;
